import { useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Upload, Download, RefreshCw, Search, X, SlidersHorizontal, Eye, ChevronDown, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useSecrets } from '../hooks/useSecrets';
import { useProjects } from '../hooks/useProjects';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useMyPermissions, canManagePermissions } from '../hooks/usePermissions';
import { useToast } from '../components/ui/toast';
import { SecretsList } from '../components/secrets/SecretsList';
import { EnvironmentTabs } from '../components/secrets/EnvironmentTabs';
import { DeleteConfirmDialog } from '../components/secrets/DeleteConfirmDialog';
import { AddSecretModal } from '../components/secret-management/AddSecretModal';
import { BulkImportModal } from '../components/secret-management/BulkImportModal';
import { SecretDetailDrawer } from '../components/secrets/SecretDetailDrawer';
import { CreateEnvironmentModal } from '../components/environments';
import { EnvironmentPermissionsModal } from '../components/permissions';
import { PlanLimitWarning, UsageBadge } from '../components/billing';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

type SourceFilter = 'all' | 'local' | 'inherited';
type UpdatedFilter = 'all' | 'today' | 'week' | 'month';

interface Filters {
  source: SourceFilter;
  updated: UpdatedFilter;
}

export function SecretsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const environmentSlug = searchParams.get('env');
  const toast = useToast();

  const { selectedProject, environments, isLoadingEnvironments, createEnvironment, primaryTeamId } = useProjects();
  const actualProjectId = projectId || selectedProject?.id || null;
  const actualEnvironmentSlug = environmentSlug || environments[0]?.name || null;

  // Plan limits
  const { secretsUsage, canCreateSecret, canCreateEnvironment, refetchUsage } = usePlanLimits(primaryTeamId);
  const isAtSecretLimit = !canCreateSecret();
  const isAtEnvLimit = !canCreateEnvironment();

  // Permissions
  const { data: myPermissions } = useMyPermissions(actualProjectId);
  const userCanManagePermissions = actualEnvironmentSlug
    ? canManagePermissions(myPermissions, actualEnvironmentSlug)
    : false;

  const {
    secrets,
    isLoading,
    refetch,
    getSecret,
    createSecret,
    updateSecret,
    deleteSecret,
    bulkImport,
    downloadEnvFile,
    isExporting,
    isDeleting,
  } = useSecrets(actualProjectId, actualEnvironmentSlug);

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Filters>({
    source: 'all',
    updated: 'all',
  });
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [revealedSecrets, setRevealedSecrets] = useState<Map<string, string>>(new Map());
  const [loadingReveal, setLoadingReveal] = useState<Set<string>>(new Set());
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [isCreateEnvModalOpen, setIsCreateEnvModalOpen] = useState(false);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [permissionsModalEnv, setPermissionsModalEnv] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ key?: string; bulkCount?: number } | null>(null);
  const [drawerSecretKey, setDrawerSecretKey] = useState<string | null>(null);
  const drawerSecret = secrets.find((s) => s.key === drawerSecretKey) || null;

  // Apply filters to secrets
  const filteredSecrets = useMemo(() => {
    return secrets.filter((secret) => {
      // Source filter
      if (filters.source === 'local' && secret.inherited_from) return false;
      if (filters.source === 'inherited' && !secret.inherited_from) return false;

      // Updated filter
      if (filters.updated !== 'all') {
        const updatedAt = new Date(secret.updated_at);
        const now = new Date();
        const diffMs = now.getTime() - updatedAt.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);

        if (filters.updated === 'today' && diffDays > 1) return false;
        if (filters.updated === 'week' && diffDays > 7) return false;
        if (filters.updated === 'month' && diffDays > 30) return false;
      }

      return true;
    });
  }, [secrets, filters]);

  const activeFilterCount = [
    filters.source !== 'all',
    filters.updated !== 'all',
  ].filter(Boolean).length;

  const clearFilters = () => {
    setFilters({ source: 'all', updated: 'all' });
  };

  const handleSelectEnvironment = (envId: string) => {
    const env = environments.find((e) => e.id === envId);
    if (env) {
      setSearchParams({ env: env.name });
      setRevealedSecrets(new Map());
      setSelectedKeys(new Set());
      setEditingKey(null);
      setDrawerSecretKey(null);
    }
  };

  const handleCreateEnvironment = async (data: { name: string; slug: string; inherits_from?: string }) => {
    await createEnvironment({ name: data.name, inherits_from: data.inherits_from });
    toast.success(`Environment "${data.name}" created`);
    setIsCreateEnvModalOpen(false);
    setSearchParams({ env: data.name });
    refetchUsage(); // Update usage counts
  };

  const handleOpenCreateEnv = () => {
    if (isAtEnvLimit) return;
    setIsCreateEnvModalOpen(true);
  };

  const handleManageAccess = (environmentName: string) => {
    setPermissionsModalEnv(environmentName);
    setIsPermissionsModalOpen(true);
  };

  const handleClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false);
    setPermissionsModalEnv(null);
  };

  const selectedEnvironmentId = environments.find((e) => e.name === actualEnvironmentSlug)?.id;

  const handleSelect = useCallback((key: string, selected: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const handleReveal = useCallback(
    async (key: string) => {
      setLoadingReveal((prev) => new Set(prev).add(key));
      try {
        const secret = await getSecret(key);
        setRevealedSecrets((prev) => new Map(prev).set(key, secret.value));
      } catch {
        toast.error('Failed to reveal secret');
      } finally {
        setLoadingReveal((prev) => {
          const n = new Set(prev);
          n.delete(key);
          return n;
        });
      }
    },
    [getSecret, toast]
  );

  const handleHide = useCallback((key: string) => {
    setRevealedSecrets((prev) => {
      const n = new Map(prev);
      n.delete(key);
      return n;
    });
  }, []);

  const handleCopy = useCallback(
    async (key: string) => {
      let value = revealedSecrets.get(key);
      if (!value) {
        try {
          const s = await getSecret(key);
          value = s.value;
          setRevealedSecrets((prev) => new Map(prev).set(key, value!));
        } catch {
          toast.error('Failed to get secret value');
          return;
        }
      }
      try {
        await navigator.clipboard.writeText(value);
        toast.success('Copied to clipboard');
      } catch {
        toast.error('Failed to copy');
      }
    },
    [revealedSecrets, getSecret, toast]
  );

  const handleEdit = useCallback(
    async (key: string) => {
      if (!revealedSecrets.has(key)) await handleReveal(key);
      setEditingKey(key);
    },
    [revealedSecrets, handleReveal]
  );

  const handleSave = useCallback(
    async (key: string, value: string, description?: string) => {
      setIsSaving(true);
      try {
        await updateSecret({ key, value, description });
        setRevealedSecrets((prev) => new Map(prev).set(key, value));
        setEditingKey(null);
        toast.success('Secret updated');
      } catch {
        toast.error('Failed to save');
      } finally {
        setIsSaving(false);
      }
    },
    [updateSecret, toast]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingKey(null);
  }, []);

  const handleOpenDrawer = useCallback(
    async (key: string) => {
      if (!revealedSecrets.has(key)) await handleReveal(key);
      setDrawerSecretKey(key);
    },
    [revealedSecrets, handleReveal]
  );

  const handleDelete = useCallback((key: string) => {
    setDeleteTarget({ key });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.key) {
        await deleteSecret(deleteTarget.key);
        setRevealedSecrets((prev) => {
          const n = new Map(prev);
          n.delete(deleteTarget.key!);
          return n;
        });
        toast.success('Secret deleted');
      } else if (deleteTarget.bulkCount) {
        await Promise.all(Array.from(selectedKeys).map((key) => deleteSecret(key)));
        setSelectedKeys(new Set());
        setRevealedSecrets(new Map());
        toast.success(`Deleted ${deleteTarget.bulkCount} secrets`);
      }
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete');
    }
  }, [deleteTarget, selectedKeys, deleteSecret, toast]);

  const handleAddSecret = async (data: { key: string; value: string; description?: string }) => {
    try {
      await createSecret(data);
      toast.success('Secret created');
      setIsAddModalOpen(false);
      refetchUsage(); // Update usage counts
    } catch {
      toast.error('Failed to create secret');
      throw new Error();
    }
  };

  const handleOpenAddSecret = () => {
    if (isAtSecretLimit) return;
    setIsAddModalOpen(true);
  };

  const handleBulkImport = async (
    secretsToImport: Array<{ key: string; value: string }>,
    overwrite: boolean
  ) => {
    try {
      const r = await bulkImport({ secrets: secretsToImport, overwrite });
      const total = r.created + r.updated;
      toast.success(`Imported ${total} secret${total !== 1 ? 's' : ''}`);
      setIsBulkImportModalOpen(false);
      return r;
    } catch {
      toast.error('Failed to import');
      throw new Error();
    }
  };

  const handleExport = async () => {
    try {
      await downloadEnvFile(actualEnvironmentSlug ? `.env.${actualEnvironmentSlug}` : '.env');
      toast.success('Exported successfully');
    } catch {
      toast.error('Failed to export');
    }
  };

  const handleRevealAll = async () => {
    for (const secret of secrets) {
      if (!revealedSecrets.has(secret.key)) {
        await handleReveal(secret.key);
      }
    }
  };

  const handleHideAll = () => {
    setRevealedSecrets(new Map());
  };

  const filteredCount = searchQuery
    ? filteredSecrets.filter((s) => s.key.toLowerCase().includes(searchQuery.toLowerCase())).length
    : filteredSecrets.length;

  if (!actualProjectId) {
    return (
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-muted/50 mb-4">
                <FolderOpen className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                Select a project from the sidebar to manage secrets
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-5"
      >
        <EnvironmentTabs
          environments={environments}
          selectedEnvironmentId={selectedEnvironmentId}
          onSelectEnvironment={handleSelectEnvironment}
          onCreateEnvironment={handleOpenCreateEnv}
          onManageAccess={handleManageAccess}
          isLoading={isLoadingEnvironments}
          isAtLimit={isAtEnvLimit}
          canManagePermissions={userCanManagePermissions}
        />
      </motion.div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="flex items-center justify-between gap-4 mb-5"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold tracking-tight">
            Secrets
          </h2>
          {secretsUsage ? (
            <UsageBadge usage={secretsUsage} />
          ) : (
            <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
              {filteredCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/30">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => refetch()}
              title="Refresh"
              className="hover:bg-muted/50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  title="Filter"
                  className="hover:bg-muted/50 relative"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-medium text-primary-foreground flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-3">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Filters</span>
                    {activeFilterCount > 0 && (
                      <Button variant="ghost" size="sm" className="h-auto py-1 px-2 text-xs" onClick={clearFilters}>
                        Clear all
                      </Button>
                    )}
                  </div>

                  {/* Source filter */}
                  <div className="space-y-2">
                    <span className="text-xs text-muted-foreground font-medium">Source</span>
                    <div className="grid grid-cols-3 gap-1">
                      {(['all', 'local', 'inherited'] as const).map((value) => (
                        <Button
                          key={value}
                          variant={filters.source === value ? 'secondary' : 'ghost'}
                          size="sm"
                          className="h-7 text-xs capitalize"
                          onClick={() => setFilters((f) => ({ ...f, source: value }))}
                        >
                          {value}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Updated filter */}
                  <div className="space-y-2">
                    <span className="text-xs text-muted-foreground font-medium">Updated</span>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { value: 'all' as const, label: 'Any time' },
                        { value: 'today' as const, label: 'Today' },
                        { value: 'week' as const, label: 'This week' },
                        { value: 'month' as const, label: 'This month' },
                      ].map(({ value, label }) => (
                        <Button
                          key={value}
                          variant={filters.updated === value ? 'secondary' : 'ghost'}
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setFilters((f) => ({ ...f, updated: value }))}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" title="Visibility" className="hover:bg-muted/50">
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass">
                <DropdownMenuItem onClick={handleRevealAll}>
                  Reveal all values
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleHideAll}>
                  Hide all values
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-colors group-focus-within:text-primary" />
            <Input
              type="text"
              placeholder="Search secrets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-[200px] bg-muted/30 border-transparent focus:border-primary/50 focus:bg-muted/50 transition-all"
            />
            <AnimatePresence>
              {searchQuery && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                >
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setSearchQuery('')}
                    className="h-6 w-6"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-1.5 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-shadow">
                <Plus className="h-4 w-4" />
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass">
              <DropdownMenuItem onClick={handleOpenAddSecret} disabled={isAtSecretLimit}>
                <Plus className="h-4 w-4 mr-2" />
                Add Secret
                {isAtSecretLimit && <span className="ml-auto text-xs text-destructive">Limit</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsBulkImportModalOpen(true)} disabled={isAtSecretLimit}>
                <Upload className="h-4 w-4 mr-2" />
                Import from .env
                {isAtSecretLimit && <span className="ml-auto text-xs text-destructive">Limit</span>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExport} disabled={isExporting || secrets.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Export as .env
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
              <CardContent className="p-0">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex border-b border-border/50 last:border-b-0">
                    <div className="w-[340px] px-4 py-4 border-r border-border/50 bg-muted/10">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-muted/50 animate-shimmer" />
                        <div className="space-y-2 flex-1">
                          <div className="h-4 w-24 bg-muted/50 rounded animate-shimmer" />
                          <div className="h-3 w-12 bg-muted/30 rounded animate-shimmer" />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 px-4 py-4">
                      <div className="h-4 w-32 bg-muted/30 rounded animate-shimmer" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <SecretsList
              secrets={filteredSecrets}
              selectedKeys={selectedKeys}
              revealedSecrets={revealedSecrets}
              loadingReveal={loadingReveal}
              editingKey={editingKey}
              isSaving={isSaving}
              environmentSlug={actualEnvironmentSlug || 'development'}
              onSelect={handleSelect}
              onReveal={handleReveal}
              onHide={handleHide}
              onCopy={handleCopy}
              onEdit={handleEdit}
              onSave={handleSave}
              onCancelEdit={handleCancelEdit}
              onOpenDrawer={handleOpenDrawer}
              onDelete={handleDelete}
              searchQuery={searchQuery}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Limit warning banner */}
      {secretsUsage && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4"
        >
          <PlanLimitWarning
            resourceType="secrets"
            usage={secretsUsage}
            variant="banner"
            onUpgradeClick={() => navigate('/billing')}
          />
        </motion.div>
      )}

      {/* Floating add button - hide when at limit */}
      {!isAtSecretLimit && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed bottom-8 right-8"
        >
          <Button
            size="lg"
            className="gap-2 shadow-2xl shadow-primary/30 hover:shadow-primary/40 transition-all hover:scale-105 active:scale-95"
            onClick={handleOpenAddSecret}
          >
            <Sparkles className="h-4 w-4" />
            Add Secret
          </Button>
        </motion.div>
      )}

      {actualProjectId && actualEnvironmentSlug && (
        <SecretDetailDrawer
          isOpen={!!drawerSecretKey}
          onClose={() => setDrawerSecretKey(null)}
          secret={drawerSecret}
          currentValue={drawerSecretKey ? revealedSecrets.get(drawerSecretKey) : undefined}
          projectId={actualProjectId}
          environmentSlug={actualEnvironmentSlug}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
      <AddSecretModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddSecret}
        existingKeys={secrets.map((s) => s.key)}
      />
      <BulkImportModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onImport={handleBulkImport}
        existingKeys={secrets.map((s) => s.key)}
      />
      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        secretKey={deleteTarget?.key}
        bulkCount={deleteTarget?.bulkCount}
        isDeleting={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
      <CreateEnvironmentModal
        isOpen={isCreateEnvModalOpen}
        onClose={() => setIsCreateEnvModalOpen(false)}
        onCreate={handleCreateEnvironment}
        existingSlugs={environments.map((e) => e.name.toLowerCase())}
        existingEnvironments={environments.map((e) => ({
          id: e.id,
          name: e.name,
          inherits_from: e.inherits_from,
        }))}
      />
      {actualProjectId && permissionsModalEnv && (
        <EnvironmentPermissionsModal
          isOpen={isPermissionsModalOpen}
          onClose={handleClosePermissionsModal}
          projectId={actualProjectId}
          environmentName={permissionsModalEnv}
          teamId={primaryTeamId}
        />
      )}
    </div>
  );
}

function FolderOpen(props: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
    </svg>
  );
}
